import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { getCityPageBySlug } from "@/data/citiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Neuilly + 5 bullets ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison locale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous simplifier la préparation de votre déménagement, nous
            assurons la livraison de vos cartons et fournitures directement à
            {" "}{city.name}. Inutile de vous déplacer ou de transporter des
            emballages volumineux : nous vous apportons tout le matériel
            nécessaire à l&apos;adresse de votre choix.
          </p>
        </div>

        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            "Livraison rapide de cartons, adhésifs et accessoires d'emballage.",
            "Fournitures soigneusement préparées avant expédition.",
            "Cartons livrés en parfait état, prêts à être utilisés.",
            "Service fiable et adapté aux besoins des particuliers comme des professionnels.",
            "Réception de votre commande à domicile ou sur votre lieu d'activité.",
          ].map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 bg-white rounded-xl border border-[#cfdbe6] px-4 py-3.5"
            >
              <CircleCheck
                className="h-5 w-5 text-[#237cb5] mt-0.5 shrink-0"
                strokeWidth={2.4}
              />
              <span className="text-[14px] text-[#3a4856] leading-relaxed">
                {item}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
          Profitez d&apos;une solution pratique pour recevoir rapidement vos
          fournitures et organiser votre déménagement dans les meilleures
          conditions.
        </p>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés -40% (usage unique) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Économie circulaire premium
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              L&apos;économie circulaire premium : nos cartons reconditionnés à
              {" "}<span className="text-[#237cb5]">-40 %</span> (usage unique garanti)
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce qu&apos;allier rationalisation budgétaire et démarche
              écoresponsable est devenu une priorité, nous avons développé une
              filière exclusive de cartons de seconde main haut de gamme,
              affichant une réduction allant jusqu&apos;à 40&nbsp;% par
              rapport au neuf.
            </p>
            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Sélection à usage unique",
                  body: "Nous collectons uniquement des emballages qui n'ont servi qu'une seule et unique fois, issus de circuits logistiques propres. Ils ne présentent aucune usure structurelle.",
                },
                {
                  title: "Contrôle d'intégrité strict",
                  body: "Chaque module reconditionné subit une inspection manuelle drastique par nos techniciens. Nous validons l'adhérence des fibres, l'absence de micro-fissures et la parfaite tenue de la double cannelure. Vous bénéficiez d'une résistance à l'empilement rigoureusement identique à celle d'un produit sortant d'usine.",
                },
              ].map(({ title, body }) => (
                <div key={title} className="flex items-start gap-3">
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white shrink-0 mt-0.5">
                    <CircleCheck className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                      {title}
                    </h3>
                    <p className="mt-1 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-neuilly-cta-reconditionne"
            >
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                -40 %
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Usage unique vérifié, double cannelure intacte. La performance
              du neuf, le prix d&apos;une démarche éco-responsable.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 4 — Tarification compétitive ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#FF7A00" }}>
            Au juste prix
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Tarification compétitive : la qualité industrielle au juste prix
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Notre positionnement est clair : démocratiser l&apos;accès au
            matériel utilisé par les déménageurs professionnels sans surtaxer
            nos clients. Nous vous garantissons un rapport qualité-prix
            optimal pour sécuriser vos préparatifs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Tarifs directs d'usine",
              body: "En supprimant les intermédiaires commerciaux et en optimisant nos volumes de stockage en Île-de-France, nous parvenons à compresser nos coûts pour vous offrir des tarifs ultra-compétitifs sur des cartons renforcés.",
              Icon: Tag,
            },
            {
              title: "Transparence contractuelle",
              body: "Chez nous, aucune fluctuation tarifaire imprévue ni frais dissimulés lors de la livraison. Vous investissez de manière précise dans un blindage pro, dimensionné pour éliminer définitivement les risques de casse et les mauvaises surprises le jour J.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-neuilly-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-neuilly-cta-kits"
          >
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Autres zones du 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos autres zones
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos autres zones de livraison de cartons dans les Hauts-de-Seine
            ({city.deptCode})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Vous habitez à la limite de {city.name} ou vous changez de commune
            au sein du département&nbsp;? Notre service logistique quadrille
            quotidiennement la région pour acheminer vos fournitures et
            piloter vos projets&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement/${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-neuilly-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
